import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';

const devUrl = environment.backendurl;

/**
 * Data structure of each request object for talent insights export
 */
export interface ExportRequest {
    type: string
    action?: string
    data: {
        currentTab: string
    }
}

/**
 * Data structure of each service payload object for talent insights export
 */
export interface ExportPayload {
    type?: string
    tab: string
    totalresultcount?: number
    data?: any
    aiData?: string
    aiRequestFilters?: any
    aiRequestPayload?: any[]
    [key: string]: any
}

export interface SkillTaxonomy {
  jobtitles: string[]
  skills:  {skill_name: string, skill_count: number}[]
}

export interface JobAggregatesPayload {
  startindex?: number;
  resultsize?: number;
  jobtitles?: string[];
  skills?: string[];
  locations?: string[];
  industries?: string[];
  companies?: string[];
  aggregatetype: string;
  aggregatescount?: number;
}

@Injectable({
  providedIn: 'root'
})

export class TalentInsightsService {
    public companyData: any

    /**
     * Observable for handling insights-export request signal
     */
    private insightsExportRequestObservable: Subject<any> = new Subject();
    /**
     * Observable for handling insights-export data sharing
     */
    private insightsExportDataObservable: Subject<any> = new Subject();

    /**
     * Cache of skill taxonomy api
     */
    private skillTaxonomyCache: { [key: string]: Observable<any> } = {}

    aiSuggestions: any = {};

    private aiSuggestionDataObservable: Subject<any> = new Subject();

    /**
     * Tabs of Talent insights tab
     */
    public INSIGHTS_TABS = {
        OVERVIEW: 'overview',
        LOCATION: 'location',
        COMPANY: 'company',
        EDUCATION: 'education',
        SKILL: 'skills',
        INDUSTRIES: 'industries',
        TITLES: 'titles',
        PROFILES: 'profiles',
        DIVERSITY: 'diversity'
    }
    /**
     * Mapping of talent insights tab with there tab name
     */
    public TALENT_INSIGHTS_TAB_URL_MAPPING =  {
        '/report-details/report-overview': this.INSIGHTS_TABS.OVERVIEW,
        '/report-details/report-location': this.INSIGHTS_TABS.LOCATION,
        '/report-details/report-company': this.INSIGHTS_TABS.COMPANY,
        '/report-details/report-education': this.INSIGHTS_TABS.EDUCATION,
        '/report-details/report-skills': this.INSIGHTS_TABS.SKILL,
        '/report-details/report-industries': this.INSIGHTS_TABS.INDUSTRIES,
        '/report-details/report-titles': this.INSIGHTS_TABS.TITLES,
        '/report-details/report-profiles': this.INSIGHTS_TABS.PROFILES,
        '/report-details/report-diversity': this.INSIGHTS_TABS.DIVERSITY
      }


    constructor(private http: HttpClient, private router: Router) { }

    /** Get current active tab of talent insights */
    get currentTabTalentInsights(): string{
        let currentlyActiveTab = '';
        const obj = this.TALENT_INSIGHTS_TAB_URL_MAPPING;
        Object.keys(obj).forEach(key => {
          if(this.router.url.includes(key)){
            currentlyActiveTab = obj[key];
          }
        });
        return currentlyActiveTab
    }

    /**
     * Getter for insights-export request observable
     */
    get insightsExportRequestEvent(): Observable<any> {
        return this.insightsExportRequestObservable.asObservable();
    }
    /**
     * Getter for insights-export request observable
     */
    get insightsExportDataEvent(): Observable<any> {
        return this.insightsExportDataObservable.asObservable();
    }

    get aiSuggesionData(): Observable<any>  {
        return this.aiSuggestionDataObservable.asObservable();
    }
    /*
     * Send request for insights export
     * @param requestData
     */
    sendInsightsExportRequest(requestData: ExportRequest) {
        this.insightsExportRequestObservable.next(requestData);
    }
    /**
     * Emit data for insights export
     * @param exportData
     */
    sendInsightsExportData(exportData: ExportPayload) {
        this.insightsExportDataObservable.next(exportData);
    }

    sendAISuggestionData(data: any) {
        this.aiSuggestionDataObservable.next(data);
    }

    /**
     * Export talent insights report to PDF
     * @param payload
     */
    exportInsightsToPDFReport(payload: any): Observable<any> {
        const url = devUrl + `/personsearch/talent/insights/export`;
        return this.http.post(url, payload).pipe(map(response => { return response; })
        , catchError(GlobalService.handleError('Company Insights Export to PDF', url)));
    }

    /**
     * Export talent insights report to Excel
     * @param payload
     */
    exportInsightsToExcelReport(payload: any): Observable<any> {
        const url = devUrl + `/personsearch/talent/insights/export-excel`;
        return this.http.post(url, payload).pipe(map(response => { return response; })
        , catchError(GlobalService.handleError('Company Insights Export to Excel', url)));
    }

    /**
     * Get skill taxonomy data
     * @returns Observable<any>
     */
    getSkillTaxonomy(payload:SkillTaxonomy): Observable<any> {
        const url = devUrl + '/skill-taxonomy';
        const hash = CryptoJS.MD5(JSON.stringify(payload)).toString();
        if(Reflect.has(this.skillTaxonomyCache, hash)){
            // console.log('cache hit');
            return of(this.skillTaxonomyCache[hash])
        }
        return this.http.post(url, payload).pipe(
            map(response => response),
            tap((d:any) => {
                  this.skillTaxonomyCache[hash] = d
            }),
            catchError(GlobalService.handleError('Get Skill Taxonomy', url))
        );

      }

  getJobsAggregates(payload:JobAggregatesPayload): Observable<JobAggregatesPayload> {
    const url = devUrl + `/jobs/aggregates`;
    return this.http.post<JobAggregatesPayload>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Jobs Aggregates', url)));
}

getCountryCurrency() {
    const url = devUrl + `/country/currency-rate`;
    return this.http.get(url).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Country Currency', url)));
}

getJobDemandCount(payload: any): Observable<any> {
    const url = devUrl + `/jobs/demand/count`;
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Job Demand Count', url)));
}

getLocationCurrency(payload:any) {
    const url = devUrl + `/location/currency`;
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Location Currency', url)));
}


  setAISuggestionData(data) {
    this.aiSuggestions = Object.assign({}, data);
  }

 getAISuggestionData() {
    return Object.assign({}, this.aiSuggestions);
  }

getJoblist(data){
  const url = devUrl + `/jobs/list`;
    return this.http.post(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Location Currency', url)));
}

}
